import { Box, Button } from '@cowsquare/design';
import { themeGet } from '@styled-system/theme-get';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import { Link, navigate } from 'gatsby';
import * as R from 'ramda';
import React, { useCallback, useEffect, useState } from 'react';
import posed, { PoseGroup } from 'react-pose';
import styled from 'styled-components';
import { layout } from 'styled-system';
import { useUser } from '../../../utils/hooks';
import { isBrowser } from '../../../utils/isBrowser';
import { Mask } from '../../Mask';
import Logo from '../Logo';
import { DesktopMenu, DesktopUser } from './DesktopMenu';
import { CloseIcon, MenuIcon } from './Icons';
import { MobileMenu, MobileUser } from './MobileMenu';
import { roleMenu, rolePages } from './roles';
import { MobileRoleDropdown, RoleDropdown } from './RoleDropdown';

const Content = styled.header`
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  width: 100%;
  height: 60px;
  background-color: white;
  z-index: ${themeGet('zIndex.sticky')};
`;

const Block = styled(Box)`
  ${layout}
`;

const SideMenuAnimation = posed.div({
  enter: {
    x: 0,
    transition: { duration: 300, ease: 'circOut' },
  },
  exit: {
    x: '100%',
    transition: { duration: 200, ease: 'circIn' },
    delay: 0,
  },
});

const SideMenu = styled(SideMenuAnimation)`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  width: 312px;
  height: 100%;
  overflow: auto;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: ${themeGet('zIndex.fixed')};
  -webkit-overflow-scrolling: touch;
`;

const SideMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid #e5e5e5;
`;

const SideMenuButton = styled(Button).attrs({
  variant: 'link',
})`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
  padding: 0;
`;

const Header = ({ role }) => {
  const targetRef = React.createRef();
  const { userInfo: user, HKDUUserInfo } = useUser();
  const avatar = R.propOr('', 'real_avatar')(HKDUUserInfo);
  const routerRole = R.find(R.propEq('value', role), rolePages);

  const [currentRole, setCurrentRole] = useState(routerRole || rolePages[0]);
  const onRoleChange = useCallback(item => {
    navigate(`/${item.value}/`);
    setCurrentRole(item);
  }, []);

  if (!R.propEq('value', role, currentRole) && routerRole) {
    setCurrentRole(routerRole);
  }

  const menu = R.propOr([], currentRole.value, roleMenu);

  const [isMenuOpen, setMenuOpen] = useState(false);
  const openMenu = useCallback(() => {
    setMenuOpen(true);
  }, []);
  const closeMenu = useCallback(() => {
    clearAllBodyScrollLocks();
    setMenuOpen(false);
  }, []);

  const clickMobileRole = useCallback(
    role => {
      onRoleChange(role);
      closeMenu();
    },
    [closeMenu, onRoleChange]
  );

  useEffect(() => {
    if (isMenuOpen) {
      disableBodyScroll(targetRef.current);
    }
  }, [isMenuOpen, targetRef]);

  return (
    <Content>
      <Box ml="15px">
        <Link to={`/${currentRole.value}/`}>
          <Logo />
        </Link>
      </Box>
      {/* --- Desktop Menu --- */}
      <Block width={1} display={['none', 'flex']}>
        {user && (
          <Box ml="15px">
            <RoleDropdown options={rolePages} onChange={onRoleChange} selectedItem={currentRole} />
          </Box>
        )}
        <Box ml="15px">{isBrowser && <DesktopMenu menu={menu} />}</Box>
        <Box ml="auto" mr="20px">
          <DesktopUser user={user} avatar={avatar} />
        </Box>
      </Block>
      {/* --- Mobile Menu --- */}
      <Block ml="auto" mr={20} display={['flex', 'none']}>
        <SideMenuButton onClick={openMenu}>
          <MenuIcon />
        </SideMenuButton>
        <PoseGroup>
          {isMenuOpen && [
            <Mask key="mask" onClick={closeMenu} />,
            <SideMenu key="mobile-menu" ref={targetRef}>
              {/* this div is for safari's bug */}
              <div>
                <SideMenuHeader>
                  <SideMenuButton onClick={closeMenu}>
                    <CloseIcon />
                  </SideMenuButton>
                </SideMenuHeader>
              </div>
              <MobileRoleDropdown options={rolePages} onChange={clickMobileRole} selectedItem={currentRole} />
              <MobileMenu menu={menu} closeMenu={closeMenu} />
              {/* this div is for safari's bug */}
              <div>
                <MobileUser user={user} closeMenu={closeMenu} />
              </div>
            </SideMenu>,
          ]}
        </PoseGroup>
      </Block>
    </Content>
  );
};

export default Header;
